import { FC, useState } from 'react';
import { createColumnHelper, ColumnDef, Row } from '@tanstack/react-table';
import {
  Tag,
  Dropdown,
  Link as CarbonLink,
  TextInput,
  Button,
  InlineNotification,
} from '@carbon/react';
import { Search, Close } from '@carbon/react/icons';
import { useAtom } from 'jotai';
import { PageHeader } from 'components/page-header';
import { StatsBar } from 'components/stats-bar';
import { DataTable } from 'components/data-table';
import { LaunchLink } from 'components/launch-link';
import {
  hoursAtom,
  evalIdAtom,
  packageIdAtom,
  eval2RunsStatusAtom,
} from './atoms';
import { DevAreaEvalsTable } from './dev-area-evals-table';
import { parseLocalDateTime } from 'utils/dates';
import type {
  Eval2RunStatus,
  LookbackOption,
  LongRunningQuery,
  StatusOption,
} from './types';
import styles from './page.module.scss';

// Lookback options
const LOOKBACK_OPTIONS: LookbackOption[] = [
  { id: '24', label: 'Last 24 hours' },
  { id: '48', label: 'Last 48 hours' },
  { id: '72', label: 'Last 72 hours' },
  { id: '168', label: 'Last 7 days' },
  { id: '720', label: 'Last 30 days' },
];

// Status filter options
const STATUS_OPTIONS: StatusOption[] = [
  { id: 'all', label: 'All Statuses' },
  { id: 'success', label: 'Success' },
  { id: 'pending', label: 'Pending' },
  { id: 'error', label: 'Error' },
  { id: 'empty', label: 'Empty' },
];

// Helper to get status from counts
const getStatus = (
  row: Eval2RunStatus
): 'success' | 'pending' | 'error' | 'empty' => {
  if (row.isSuccess) return 'success';
  if (row.errorCount > 0) return 'error';
  if (row.pendingCount > 0) return 'pending';
  if (row.totalCount === 0) return 'empty';
  return 'success';
};

const STATUS_CONFIG = {
  success: { label: 'Success', tagType: 'green' },
  pending: { label: 'Pending', tagType: 'blue' },
  error: { label: 'Error', tagType: 'red' },
  empty: { label: 'Empty', tagType: 'gray' },
} as const;

const PACKAGE_TYPE_LABELS: Record<string, string> = {
  OpportunityPackage: 'Opportunity',
  AccountPackage: 'Account',
};

// Column helper for main table
const columnHelper = createColumnHelper<Eval2RunStatus>();

export const baseColumns = [
  columnHelper.accessor('id', {
    id: 'id',
    header: 'ID',
    size: 80,
    cell: (info) => {
      const value = info.getValue();
      return (
        <CarbonLink
          href={`/package_evaluations/${value}`}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          {value}
        </CarbonLink>
      );
    },
  }),
  columnHelper.accessor('packageId', {
    id: 'packageId',
    header: 'Package',
    size: 100,
    cell: (info) => {
      const value = info.getValue();
      return (
        <CarbonLink
          href={`/packages/${value}`}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          {value}
        </CarbonLink>
      );
    },
  }),
  columnHelper.accessor('packageName', {
    id: 'packageName',
    header: 'Package Name',
    size: 150,
    meta: { type: 'text' },
    cell: (info) => info.getValue() || '--',
  }),
  columnHelper.accessor('packageType', {
    id: 'packageType',
    header: 'Type',
    size: 100,
    meta: {
      filterOptions: [
        { value: 'OpportunityPackage', label: 'Opportunity' },
        { value: 'AccountPackage', label: 'Account' },
      ],
    },
    cell: (info) => {
      const value = info.getValue();
      return value ? PACKAGE_TYPE_LABELS[value] || value : '--';
    },
  }),
  columnHelper.accessor('resourceId', {
    id: 'resourceId',
    header: 'Resource ID',
    size: 200,
    meta: { type: 'text' },
    cell: (info) => {
      const value = info.getValue();
      if (!value) return '--';

      const salesforceBaseUrl = (
        window as { CONFIG?: { salesforceBaseUrl?: string } }
      ).CONFIG?.salesforceBaseUrl;

      if (salesforceBaseUrl) {
        return (
          <LaunchLink
            href={`${salesforceBaseUrl}/${value}`}
            className={styles.link}
            label={`${value} — open in Salesforce in a new tab`}
          >
            {value}
          </LaunchLink>
        );
      }
      return value;
    },
  }),
  columnHelper.accessor('createdBy', {
    id: 'createdBy',
    header: 'Created By',
    size: 150,
    meta: { type: 'text' },
    cell: (info) => info.getValue() || '--',
  }),
  columnHelper.accessor('lookupStrategy', {
    id: 'lookupStrategy',
    header: 'Lookup',
    size: 80,
    meta: {
      filterOptions: [
        { value: 'legacy', label: 'Eval2.0' },
        { value: 'tir', label: 'Eval2.5' },
      ],
    },
    cell: (info) => {
      const value = info.getValue();
      if (!value) return '--';
      return (
        <Tag type="gray" size="sm">
          {value === 'tir' ? 'Eval2.5' : 'Eval2.0'}
        </Tag>
      );
    },
  }),
  columnHelper.accessor((row) => getStatus(row), {
    id: 'status',
    header: 'Status',
    size: 100,
    meta: {
      filterOptions: STATUS_OPTIONS.filter((o) => o.id !== 'all').map((o) => ({
        value: o.id,
        label: o.label,
      })),
    },
    cell: (info) => {
      const status = info.getValue();
      const config = STATUS_CONFIG[status];
      return (
        <Tag type={config.tagType} size="sm">
          {config.label}
        </Tag>
      );
    },
  }),
  columnHelper.accessor('successCount', {
    id: 'successCount',
    header: 'Success',
    size: 90,
    cell: (info) => (
      <span className={styles.countCell}>
        {info.getValue()}/{info.row.original.totalCount}
      </span>
    ),
  }),
  columnHelper.accessor('pendingCount', {
    id: 'pendingCount',
    header: 'Pending',
    size: 80,
    cell: (info) => {
      const value = info.getValue();
      return (
        <span className={value === 0 ? styles.zeroCount : styles.pendingCount}>
          {value}
        </span>
      );
    },
  }),
  columnHelper.accessor('errorCount', {
    id: 'errorCount',
    header: 'Errors',
    size: 70,
    cell: (info) => {
      const value = info.getValue();
      return (
        <span className={value === 0 ? styles.zeroCount : styles.errorCount}>
          {value}
        </span>
      );
    },
  }),
  columnHelper.accessor('assetsCount', {
    id: 'assetsCount',
    header: 'Assets',
    size: 75,
    meta: { type: 'integer' },
  }),
  columnHelper.accessor('allocationsCount', {
    id: 'allocationsCount',
    header: 'Allocations',
    size: 105,
    meta: { type: 'integer' },
  }),
  columnHelper.accessor('uniqueSlotsCount', {
    id: 'uniqueSlotsCount',
    header: 'Slots',
    size: 80,
    meta: { type: 'integer' },
  }),
  columnHelper.accessor('slotAllocationsCount', {
    id: 'slotAllocationsCount',
    header: 'Slot Allocs',
    size: 100,
    meta: { type: 'integer' },
  }),
  columnHelper.accessor('timeForCompletion', {
    id: 'timeForCompletion',
    header: 'Duration',
    size: 120,
    meta: { type: 'duration' },
  }),
  columnHelper.accessor('initialQueryDuration', {
    id: 'initialQueryDuration',
    header: 'Initial Query',
    size: 140,
    meta: { type: 'duration' },
  }),
  columnHelper.accessor('createdAt', {
    id: 'createdAt',
    header: 'Created At',
    size: 180,
    meta: { type: 'datetime' },
  }),
  columnHelper.accessor('devAreaEvalsStartTime', {
    id: 'devAreaEvalsStartTime',
    header: 'Dev Area Start',
    size: 180,
    meta: { type: 'datetime' },
  }),
  columnHelper.accessor('devAreaEvalsEndTime', {
    id: 'devAreaEvalsEndTime',
    header: 'Dev Area End',
    size: 180,
    meta: { type: 'datetime' },
  }),
];

// Column helper for long-running queries table
const queryColumnHelper = createColumnHelper<LongRunningQuery>();

const longRunningQueryColumns = [
  queryColumnHelper.accessor('pid', {
    id: 'pid',
    header: 'PID',
    size: 80,
    meta: { type: 'integer' },
    enableSorting: false,
  }),
  queryColumnHelper.accessor('durationHuman', {
    id: 'durationHuman',
    header: 'Duration',
    size: 100,
    enableSorting: false,
    cell: (info) => (
      <Tag type="red" size="sm">
        {info.getValue()}
      </Tag>
    ),
  }),
  queryColumnHelper.accessor('state', {
    id: 'state',
    header: 'State',
    size: 80,
    meta: { type: 'text' },
    enableSorting: false,
  }),
  queryColumnHelper.accessor('username', {
    id: 'username',
    header: 'User',
    size: 100,
    meta: { type: 'text' },
    enableSorting: false,
    cell: (info) => info.getValue() || '--',
  }),
  queryColumnHelper.accessor('applicationName', {
    id: 'applicationName',
    header: 'Application',
    size: 150,
    meta: { type: 'text' },
    enableSorting: false,
    cell: (info) => info.getValue() || '--',
  }),
  queryColumnHelper.accessor('waitEventType', {
    id: 'waitEventType',
    header: 'Wait Type',
    size: 100,
    meta: { type: 'text' },
    enableSorting: false,
    cell: (info) => info.getValue() || '--',
  }),
  queryColumnHelper.accessor('waitEvent', {
    id: 'waitEvent',
    header: 'Wait Event',
    size: 120,
    meta: { type: 'text' },
    enableSorting: false,
    cell: (info) => info.getValue() || '--',
  }),
  queryColumnHelper.accessor('queryStart', {
    id: 'queryStart',
    header: 'Started At',
    size: 180,
    meta: { type: 'date' },
    enableSorting: false,
    cell: (info) => {
      const value = info.getValue();
      return value ? parseLocalDateTime(value, true) : '--';
    },
  }),
  queryColumnHelper.accessor('queryPreview', {
    id: 'queryPreview',
    header: 'Query Preview',
    size: 400,
    meta: { type: 'text' },
    enableSorting: false,
    cell: (info) => (
      <code className={styles.queryPreview}>{info.getValue()}</code>
    ),
  }),
];

export const Eval2RunsStatusPage: FC = () => {
  const [hours, setHours] = useAtom(hoursAtom);
  const [evalId, setEvalId] = useAtom(evalIdAtom);
  const [packageId, setPackageId] = useAtom(packageIdAtom);
  const [loadableState] = useAtom(eval2RunsStatusAtom);
  const [idInputValue, setIdInputValue] = useState('');
  const [packageIdInputValue, setPackageIdInputValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const isLoading = loadableState.state === 'loading';
  const error =
    loadableState.state === 'hasError'
      ? loadableState.error instanceof Error
        ? loadableState.error.message
        : 'Failed to fetch data'
      : null;

  const data =
    loadableState.state === 'hasData' ? (loadableState.data?.data ?? []) : [];
  const stats =
    loadableState.state === 'hasData' ? loadableState.data?.stats : null;
  const longRunningQueries =
    loadableState.state === 'hasData'
      ? (loadableState.data?.longRunningQueries ?? [])
      : [];

  // Filter data by status if selected
  const filteredData =
    statusFilter === 'all'
      ? data
      : data.filter((row) => getStatus(row) === statusFilter);

  const handleHoursChange = ({
    selectedItem,
  }: {
    selectedItem: LookbackOption | null;
  }) => {
    if (selectedItem) {
      setHours(parseInt(selectedItem.id, 10));
      // Clear ID filters when changing hours
      setEvalId(null);
      setIdInputValue('');
    }
  };

  const handleIdSearch = () => {
    const trimmedId = idInputValue.trim();
    if (trimmedId && /^\d+$/.test(trimmedId)) {
      setEvalId(trimmedId);
      // Clear package filter when searching by eval ID
      setPackageId(null);
      setPackageIdInputValue('');
    }
  };

  const handleClearIdFilter = () => {
    setEvalId(null);
    setIdInputValue('');
  };

  const handleIdKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleIdSearch();
    }
  };

  const handlePackageIdSearch = () => {
    const trimmedId = packageIdInputValue.trim();
    if (trimmedId && /^\d+$/.test(trimmedId)) {
      setPackageId(trimmedId);
      // Clear eval ID filter when searching by package ID
      setEvalId(null);
      setIdInputValue('');
    }
  };

  const handleClearPackageIdFilter = () => {
    setPackageId(null);
    setPackageIdInputValue('');
  };

  const handlePackageIdKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handlePackageIdSearch();
    }
  };

  const handleStatusFilterChange = ({
    selectedItem,
  }: {
    selectedItem: StatusOption | null;
  }) => {
    if (selectedItem) {
      setStatusFilter(selectedItem.id);
    }
  };

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="Eval2 Runs Status"
        actions={
          <div className={styles.headerActions}>
            <div className={styles.idSearchContainer}>
              <TextInput
                id="eval-id-input"
                labelText="Eval ID"
                placeholder="Enter eval ID"
                value={idInputValue}
                onChange={(e) => setIdInputValue(e.target.value)}
                onKeyDown={handleIdKeyDown}
                size="sm"
                className={styles.idInput}
              />
              <Button
                kind="primary"
                size="sm"
                renderIcon={Search}
                iconDescription="Search by ID"
                hasIconOnly
                onClick={handleIdSearch}
                disabled={
                  !idInputValue.trim() || !/^\d+$/.test(idInputValue.trim())
                }
              />
              {evalId && (
                <Button
                  kind="ghost"
                  size="sm"
                  renderIcon={Close}
                  iconDescription="Clear ID filter"
                  hasIconOnly
                  onClick={handleClearIdFilter}
                />
              )}
            </div>
            {evalId && (
              <Tag type="blue" size="sm" className={styles.activeFilterTag}>
                Filtering by ID: {evalId}
              </Tag>
            )}
            <div className={styles.idSearchContainer}>
              <TextInput
                id="package-id-input"
                labelText="Package ID"
                placeholder="Enter package ID"
                value={packageIdInputValue}
                onChange={(e) => setPackageIdInputValue(e.target.value)}
                onKeyDown={handlePackageIdKeyDown}
                size="sm"
                className={styles.idInput}
              />
              <Button
                kind="primary"
                size="sm"
                renderIcon={Search}
                iconDescription="Search by Package ID"
                hasIconOnly
                onClick={handlePackageIdSearch}
                disabled={
                  !packageIdInputValue.trim() ||
                  !/^\d+$/.test(packageIdInputValue.trim())
                }
              />
              {packageId && (
                <Button
                  kind="ghost"
                  size="sm"
                  renderIcon={Close}
                  iconDescription="Clear Package ID filter"
                  hasIconOnly
                  onClick={handleClearPackageIdFilter}
                />
              )}
            </div>
            {packageId && (
              <Tag type="purple" size="sm" className={styles.activeFilterTag}>
                Filtering by Package: {packageId}
              </Tag>
            )}
            <Dropdown
              id="status-filter-dropdown"
              titleText="Status"
              label="Filter by status"
              items={STATUS_OPTIONS}
              itemToString={(item) => (item ? item.label : '')}
              selectedItem={
                STATUS_OPTIONS.find((opt) => opt.id === statusFilter) ??
                STATUS_OPTIONS[0]
              }
              onChange={handleStatusFilterChange}
              size="sm"
            />
            <Dropdown
              id="lookback-dropdown"
              titleText="Lookback Period"
              label="Select time range"
              items={LOOKBACK_OPTIONS}
              itemToString={(item) => (item ? item.label : '')}
              selectedItem={LOOKBACK_OPTIONS.find(
                (opt) => opt.id === String(hours)
              )}
              onChange={handleHoursChange}
              size="sm"
              disabled={!!evalId}
            />
          </div>
        }
      />
      <div className={styles.sectionHeader}>
        <div className={styles.sectionDescription}>
          Evaluation v2 run status for package evaluations. Shows development
          area evaluation progress, asset counts, allocation counts, and timing
          information.
        </div>
      </div>
      <InlineNotification
        kind="info"
        title="Nota Bene: Slots and Slot Allocations counts are still buggy."
        lowContrast
        hideCloseButton
      />
      <div
        className={
          longRunningQueries.length > 0
            ? styles.longRunningSection
            : styles.longRunningSectionClear
        }
      >
        <div
          className={
            longRunningQueries.length > 0
              ? styles.longRunningSectionHeader
              : styles.longRunningSectionHeaderClear
          }
        >
          <Tag type={longRunningQueries.length > 0 ? 'red' : 'green'} size="sm">
            {longRunningQueries.length > 0
              ? longRunningQueries.length
              : 'All Clear'}
          </Tag>
          <span
            className={
              longRunningQueries.length > 0
                ? styles.longRunningSectionTitle
                : styles.longRunningSectionTitleClear
            }
          >
            Long Running Queries (&gt; 15 minutes)
          </span>
        </div>
        {longRunningQueries.length > 0 && (
          <DataTable
            data={longRunningQueries}
            columns={longRunningQueryColumns as ColumnDef<LongRunningQuery>[]}
            getRowId={(row) => String(row.pid)}
            persistenceKey="eval2-long-running-queries"
            config={{
              sorting: false,
              globalSearch: false,
              columnFilters: false,
              export: false,
            }}
          />
        )}
      </div>
      {stats && (
        <StatsBar
          stats={[
            { label: 'Total', value: stats.total, tagType: 'gray' },
            {
              label: 'Successful',
              value: stats.successful,
              tagType: 'green',
            },
            { label: 'Pending', value: stats.pending, tagType: 'blue' },
            { label: 'Errored', value: stats.errored, tagType: 'red' },
            {
              label: 'Opportunity',
              value: stats.opportunityPackages,
              tagType: 'purple',
            },
            {
              label: 'Account',
              value: stats.accountPackages,
              tagType: 'teal',
            },
          ]}
        />
      )}
      <DataTable
        data={filteredData}
        columns={baseColumns as ColumnDef<Eval2RunStatus>[]}
        getRowId={(row) => String(row.id)}
        isLoading={isLoading}
        error={error}
        persistenceKey="eval2-runs-status"
        initialTableState={{
          sortColumn: 'createdAt',
          sortDirection: 'desc',
        }}
        config={{
          columnResizing: true,
          columnReordering: true,
          columnPinning: true,
          sorting: true,
          columnVisibility: true,
          globalSearch: true,
          columnFilters: true,
          export: {
            exportPresets: [
              { name: 'Export', format: 'csv', filename: 'eval2-runs-status' },
            ],
          },
        }}
        renderRowSubComponent={(row: Row<Eval2RunStatus>) => {
          const data = row.original;
          if (data.totalCount === 0) return null;
          return (
            <div className={styles.expandedRowWrapper}>
              <div className={styles.expandedRowHeader}>
                Dev Area Evaluations for Package Evaluation #{data.id} (
                {data.packageName})
              </div>
              <DevAreaEvalsTable packageEvaluationId={data.id} />
            </div>
          );
        }}
      />
    </>
  );
};
